import { ACTIVITY_SOURCES } from '@/utils/constants'
import { formatNumber } from '@/utils/helpers'
import { keyBy, flatten, isNil, toNumber } from 'lodash'

import dayjs from 'dayjs'
import isBetween from 'dayjs/plugin/isBetween'
import utc from 'dayjs/plugin/utc'
dayjs.extend(isBetween)
dayjs.extend(utc)

/**
 *
 * @param {Array} data
 * @returns {Array}
 */
export const mapActivity = data =>
    Object.keys(data)
        .filter(key => key !== 'id')
        .map(key => ({ source: ACTIVITY_SOURCES[key], data: data[key] }))

/**
 *
 * @param {Array} data
 * @returns {Object}
 */
export const mapAlerts = data => keyBy(data.map(mapAlert), 'id')

/**
 *
 * @param {Object} alert
 * @returns {Object}
 */
export const mapAlert = alert => ({
    createdAt: alert.created_at,
    deletedAt: alert.deleted_at,
    id: toNumber(alert.id),
    label: alert.label,
    lastEvent: alert.last_event,
    isTriggered: alert.last_event?.state === 'triggered',
    notification: JSON.parse(alert.notification),
    createdBy: JSON.parse(alert.notification)?.configuration?.to?.[0],
    objectId: alert.object_type !== 'account' ? toNumber(alert.object_id) : null,
    objectType: alert.object_type,
    status: alert.status,
    triggerCondition: JSON.parse(alert.trigger_condition),
    triggerType: alert.trigger_type,
    triggeredAt: alert.last_event ? alert.last_event.triggered_at : '',
    updatedAt: alert.updated_at
})

/**
 *
 * @param {Date} start
 * @returns {String}
 */
export const toStartISO = start =>
    dayjs(start)
        .startOf('day')
        .toISOString()

/**
 *
 * @param {Date} end
 * @returns {String}
 */
export const toEndISO = end => {
    const endISO = dayjs(end)
        .add(1, 'day')
        .startOf('day')
        .toISOString()

    return dayjs(endISO).isBefore() ? endISO : dayjs().toISOString()
}

/**
 *
 * @param {String} type 'audiences' or 'segments'
 * @returns {String} 'audience' or 'cs'
 */
export const typeToUrlParam = type => {
    const _mapper = {
        audiences: 'audience',
        segments: 'cs'
    }
    return _mapper[type]
}

/**
 * Transform alert trigger condition to a readable string
 * @param {Object} alert
 * @returns {String}
 */
export const alertConditionToString = alert => {
    const configuration = alert.triggerCondition?.configuration

    if (alert.triggerType === 'threshold') {
        const low = configuration?.low
        const high = configuration?.high
        const period = configuration?.period
        return `
                ${low ? 'Less than  ' + formatNumber(low) : ''}
                ${low && high ? 'or ' : ''}
                ${high ? 'More than ' + formatNumber(high) : ''}
                on the last ${period === 'last30days' ? '30 days' : '24 hours'}
            `
    }

    if (alert.triggerType === 'trend') {
        const low = configuration?.percentage_decrease
        const high = configuration?.percentage_increase
        const getPeriod = () => {
            const hour = configuration?.hour
            const hour_end = configuration?.hour_end

            if (hour != null && hour_end != null) return `between ${hour}h and ${hour_end}h UTC`
            else if (hour != null) return `at ${hour}h UTC`
            else return 'on the last day'
        }
        const comparedTo = configuration?.compared_to || ''

        return `
                ${low ? low + '% of decrease' : ''}
                ${low && high ? 'or ' : ''}
                ${high ? high + '% of increase ' : ''}
                ${getPeriod()}
                compared to ${comparedTo.split('_').join(' ')}
            `
    }
}

/**
 *
 * @param {Object[]} allocations
 * @param {String} timeUnit
 * @param {Object} kibanaData
 * [TODO] start & end to remove later when backend handle with timezone
 * @param {Date} start Date to filter all allocation before this day
 * @param {Date} end Date to filter all allocation after this day
 * @returns {Object}
 */
export const getAllocationMetrics = (allocations, timeUnit = 'day', kibanaData, start, end) => {
    const formatDay = 'YYYY-MM-DD'
    const formatHour = 'YYYY-MM-DD HH:mm'
    const format = timeUnit === 'day' ? formatDay : formatHour
    const startDay = dayjs(start).format(formatDay)
    const endDay = dayjs(end).format(formatDay)

    const verifyDateBetweenStandAndEndDays = pdate => dayjs(pdate).isBetween(startDay, endDay, 'day', '[]')

    // [TODO] remove filter method to remove later when backend handle with timezone
    const allAllocations = flatten(
        allocations.map(provider =>
            provider.data
                .map(({ date, value }) => {
                    return {
                        date: dayjs(date).format(format),
                        provider: provider.source.source,
                        value
                    }
                })
                .filter(provider => verifyDateBetweenStandAndEndDays(provider.date))
        )
    )

    const totalAllocations = allAllocations.reduce((total, allocation) => total + allocation.value, 0)

    const totalAllocationsKibana = isNil(kibanaData) ? 0 : kibanaData.all.reduce((total, allocation) => total + allocation.value, 0)

    const allocationByDate = allAllocations.reduce((dates, { date, value }) => {
        dates[date] = (dates[date] || 0) + value
        return dates
    }, {})

    const allocationByProvider = allAllocations.reduce((providers, { provider, value }) => {
        providers[provider] = (providers[provider] || 0) + value
        return providers
    }, {})

    // Group provider with less than 2% of allocation
    const allocationBySignificantProvider = Object.entries(allocationByProvider)
        .filter(([_provider, value]) => value > 0)
        .reduce((providers, [provider, value]) => {
            const percentage = (value * 100) / totalAllocations
            if (percentage < 2) provider = 'Other'
            providers[provider] = (providers[provider] || 0) + value
            return providers
        }, {})

    const kibanaAllocation = isNil(kibanaData)
        ? {}
        : kibanaData.all
            .map(i => [dayjs(i.date).format(format), i.value])
            .filter(([day]) => verifyDateBetweenStandAndEndDays(day))
            .reduce((obj, [date, value]) => {
                obj[date] = value
                return obj
            }, {})

    return {
        timeUnit,
        totalAllocations,
        allocationByDate,
        allocationByProvider,
        allocationBySignificantProvider,
        kibanaAllocation,
        totalAllocationsKibana
    }
}
